import React, { useEffect, useRef, useState } from "react";
import {
  webLoadingFalse,
  webNavigate,
} from "../../services/navigate.service";
import EditIconV2 from "../../images/EditIconV2";
import DeleteIconV2 from "../../images/DeleteIconV2";
import {
  deleteDeviceUserRole,
  getDeviceUserRole,
} from "../../services/deviceuserrole.service";
import { useResponsive } from "../../hook/useResponsive";
import { showApiError } from "../../services/apiError";
import { showToast } from "../../services/toast.service";
import {
  ListColumn,
  ListQuery,
  ListRowAction,
  ListScreen,
  ListScreenHandle,
  TEXT,
  cells,
  formatUpdatedAt,
} from "../../components/list";
import { DEVICE_FUNCTION_IDS } from "./roleLogic";
import { NUM, RoleDialog } from "./roleShared";
interface DeviceRoleRow {
  id: string;
  roleHirerarchy: number | string | null;
  roleDescription: string;
  restrictionDescription?: string;
  roleName: string;
  staffCount?: number;
  functionCount?: number;
  promotionCount?: number;
  updatedAt?: string | null;
}
interface DeviceRolesResponse {
  data: {
    data: {
      deviceUserRole: DeviceRoleRow[];
      count: number;
    };
  };
}
export interface DeviceUserRoleProps {
  /** ListRole's tab, kept in the URL while paging and searching. */
  tab?: string;
}
/** Device roles per company are few; load them all and search/page here. */
const ALL_ROWS = 1000;
const DeviceUserRole = ({ tab }: DeviceUserRoleProps) => {
  const { isTouchLayout } = useResponsive();
  const preLanguage = localStorage.getItem("language");
  const [language, setLanguage] = useState<any>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<ListScreenHandle>(null);
  const [deleting, setDeleting] = useState<DeviceRoleRow | null>(null);
  const [deleteBusy, setDeleteBusy] = useState<boolean>(false);
  const tr = (key: string) => (language && language[key] ? language[key] : key);
  const fill = (key: string, values: Record<string, string | number>) =>
    Object.keys(values).reduce((text, name) => text.split(`{${name}}`).join(String(values[name])), tr(key));
  const emptyState = {
    title:
      language && language?.["No device roles yet"]
        ? language?.["No device roles yet"]
        : "No device roles yet",
    desc:
      language &&
        language?.[
        "Create roles to manage permissions and access for devices within the system."
        ]
        ? language?.[
        "Create roles to manage permissions and access for devices within the system."
        ]
        : "Create roles to manage permissions and access for devices within the system.",
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === "f") {
      event.preventDefault();
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);
  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  const fetchDeviceRoles = async (query: ListQuery) => {
    webLoadingFalse();
    // One fetch, searched and paged here; core-api's searchValue now stays within the company too.
    const response: DeviceRolesResponse = await getDeviceUserRole({
      page: 1,
      limit: ALL_ROWS,
      searchValue: "",
    });
    const all = response.data.data.deviceUserRole ?? [];
    const term = query.search.trim().toLowerCase();
    const matches = term
      ? all.filter((r) =>
        [r.roleName, r.roleDescription, r.restrictionDescription].some((v) =>
          String(v ?? "").toLowerCase().includes(term)
        )
      )
      : all;
    const start = (query.page - 1) * query.limit;
    return { rows: matches.slice(start, start + query.limit), count: matches.length };
  };

  const goToDeviceRole = (id: string) =>
    webNavigate("/" + preLanguage + "/deviceUserRole/" + id);

  const handleDelete = async () => {
    if (!deleting || deleteBusy) return;
    const role = deleting;
    setDeleteBusy(true);
    try {
      await deleteDeviceUserRole(role.id);
      setDeleting(null);
      showToast({ message: fill("{name} deleted", { name: role.roleName }), type: "success", duration: 4000 });
    } catch (error) {
      showApiError(error, { language });
    } finally {
      setDeleteBusy(false);
      listRef.current?.reload();
    }
  };

  const count = (n: unknown) => Number(n ?? 0);
  const numberCell = (get: (r: DeviceRoleRow) => string) => (r: DeviceRoleRow) => (
    <span style={{ fontSize: "14px", color: TEXT, ...NUM }}>{get(r)}</span>
  );
  const staffLabel = (n: number) => (n === 1 ? tr("1 staff member") : fill("{n} staff", { n }));
  const functionsLabel = (r: DeviceRoleRow) =>
    fill("{n} of {m}", { n: count(r.functionCount), m: DEVICE_FUNCTION_IDS.length });

  const columns: ListColumn<DeviceRoleRow>[] = [
    {
      key: "roleName",
      label: "Role",
      width: 240,
      hideable: false,
      render: cells.primary((r) => r.roleName),
      value: (r) => r.roleName ?? "",
      mobile: "title",
      onClick: (r) => goToDeviceRole(String(r.id)),
    },
    {
      key: "staffCount",
      label: "Staff",
      width: 90,
      value: (r) => staffLabel(count(r.staffCount)),
      render: numberCell((r) => String(count(r.staffCount))),
      mobile: "trailing",
    },
    {
      key: "functionCount",
      label: "Till functions",
      width: 130,
      value: functionsLabel,
      render: numberCell(functionsLabel),
      mobile: "sheet",
    },
    {
      // core-api counts every linked promotion (it cannot see the type), not just manual discounts.
      key: "promotionCount",
      label: "Promotions",
      width: 110,
      value: (r) => String(count(r.promotionCount)),
      render: numberCell((r) => String(count(r.promotionCount))),
      mobile: "sheet",
    },
    {
      key: "roleDescription",
      label: "Description",
      width: 320,
      value: (r) => r.roleDescription ?? "",
      mobile: "subtitle",
    },
    {
      key: "updatedAt",
      label: "Last updated",
      width: 140,
      value: (r) => formatUpdatedAt(r.updatedAt, language),
      render: cells.date((r) => r.updatedAt),
      mobile: "sheet",
    },
  ];

  const rowActions: ListRowAction<DeviceRoleRow>[] = [
    {
      key: "edit",
      label: "Edit",
      icon: <EditIconV2 />,
      sheetIcon: "chev",
      onRun: (r) => goToDeviceRole(String(r.id)),
    },
    {
      key: "delete",
      label: "Delete",
      icon: <DeleteIconV2 />,
      sheetIcon: "close",
      danger: true,
      onRun: (r) => setDeleting(r),
    },
  ];

  const staffOnRole = count(deleting?.staffCount);

  return (
    <>
      <div
        className="h-full relative"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          flex: 1,
          minHeight: 0,
          overflowY: isTouchLayout ? "auto" : undefined,
          paddingBottom: isTouchLayout ? "24px" : undefined,
        }}
      >
        <ListScreen
          ref={listRef}
          language={language}
          columns={columns}
          fetchRows={fetchDeviceRoles}
          searchPlaceholder="Search device roles"
          searchInputRef={searchInputRef}
          rowActions={rowActions}
          countNoun="roles"
          emptyState={emptyState}
          extraUrlParams={tab ? { tab } : undefined}
        />
      </div>
      <RoleDialog
        open={!!deleting}
        touch={isTouchLayout}
        language={language}
        title={deleting ? fill("Delete {name}?", { name: deleting.roleName }) : ""}
        subtitle={deleting ? staffLabel(staffOnRole) : undefined}
        onClose={() => setDeleting(null)}
        actions={[
          { label: tr("Cancel"), onClick: () => setDeleting(null) },
          { label: tr("Delete"), variant: "danger", disabled: deleteBusy, onClick: handleDelete },
        ]}
      >
        {staffOnRole > 0 ? (
          <p role="alert" style={{ margin: 0, color: "#BA0E13", fontWeight: 500 }}>
            {fill("{n} staff use this role and will lose every till function. Move them to another role first.", { n: staffOnRole })}
          </p>
        ) : (
          <p style={{ margin: 0 }}>{tr("No staff use this role. Tills drop it at their next sync.")}</p>
        )}
      </RoleDialog>
    </>
  );
};

export default DeviceUserRole;
